import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import Modal from '../../components/Modal';
import OpeningBalanceImportModal from '../../components/OpeningBalanceImportModal';

import API_BASE from '../../utils/api';
const API = API_BASE;

const money = (value) =>
  `Rs. ${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Prefer the server-computed balanceAmount (which now includes any late
// fee) — fall back to a plain amount-paidAmount subtraction only for
// records that predate that field.
const balanceOf = (fee) =>
  fee.balanceAmount != null
    ? Number(fee.balanceAmount)
    : Math.max(Number(fee.amount || 0) - Number(fee.paidAmount || 0), 0);

const statusClass = (status) => {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-700';
    case 'partial':
      return 'bg-blue-100 text-blue-700';
    case 'overdue':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-yellow-100 text-yellow-700';
  }
};

const downloadName = (headers, fallbackName) => {
  const disposition = headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallbackName;
};

const AdminFees = () => {
  const [fees, setFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [receiptModal, setReceiptModal] = useState(null);
  const [receiptLoadingId, setReceiptLoadingId] = useState('');
  const [downloadLoadingId, setDownloadLoadingId] = useState('');
  const [reminderLoadingId, setReminderLoadingId] = useState('');
  const [bulkReminderLoading, setBulkReminderLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', studentId: '', fromDate: '', toDate: '', feeGroup: '' });
  const [feeGroups, setFeeGroups] = useState([]);
  const [showOpeningBalanceModal, setShowOpeningBalanceModal] = useState(false);
  const [paymentFee, setPaymentFee] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentRemarks, setPaymentRemarks] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [formData, setFormData] = useState({
    studentId: '',
    feeType: 'tuition',
    amount: '',
    dueDate: new Date(),
    remarks: '',
  });
  const [bulkFormData, setBulkFormData] = useState({
    classId: '',
    feeType: 'tuition',
    amount: '',
    dueDate: new Date(),
    description: '',
  });

  useEffect(() => {
    fetchStudents();
    fetchClasses();
    axios.get(`${API}/settings`).then((res) => setFeeGroups(res.data?.feeGroups || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchFees();
    fetchStats();
  }, [filters]);

  const flash = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 4000);
  };

  const fetchFees = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.studentId) params.append('studentId', filters.studentId);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      const response = await axios.get(`${API}/fees?${params.toString()}`);
      setFees(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch fee records.');
      setFees([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`);
      setStudents(Array.isArray(response.data) ? response.data : response.data?.students || []);
    } catch (err) {
      console.error(err);
      setStudents([]);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error(err);
      setClasses([]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/fees/stats/summary`);
      setStats(response.data || {});
    } catch (err) {
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({
      studentId: '',
      feeType: 'tuition',
      amount: '',
      dueDate: new Date(),
      remarks: '',
    });
    setEditingFee(null);
  };

  const resetBulkForm = () => {
    setBulkFormData({
      classId: '',
      feeType: 'tuition',
      amount: '',
      dueDate: new Date(),
      description: '',
    });
  };

  const saveFee = async (event) => {
    event.preventDefault();
    setError('');
    try {
      if (editingFee) {
        await axios.put(`${API}/fees/${editingFee._id}`, formData);
        flash('Fee record updated successfully.');
      } else {
        await axios.post(`${API}/fees`, formData);
        flash('Fee record added successfully.');
      }
      setShowModal(false);
      resetForm();
      fetchFees();
      fetchStats();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Unable to save fee record.');
    }
  };

  const saveBulkFees = async (event) => {
    event.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/fees/bulk/create`, bulkFormData);
      setShowBulkModal(false);
      resetBulkForm();
      flash('Bulk fee records created successfully.');
      fetchFees();
      fetchStats();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Unable to create bulk fee records.');
    }
  };

  const editFee = (fee) => {
    setEditingFee(fee);
    setFormData({
      studentId: fee.studentId?._id || '',
      feeType: fee.feeType || 'tuition',
      amount: fee.amount || '',
      dueDate: fee.dueDate ? new Date(fee.dueDate) : new Date(),
      remarks: fee.remarks || '',
    });
    setShowModal(true);
  };

  const deleteFee = async (feeId) => {
    if (!window.confirm('Delete this fee record?')) return;
    try {
      await axios.delete(`${API}/fees/${feeId}`);
      flash('Fee record deleted successfully.');
      fetchFees();
      fetchStats();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Unable to delete fee record.');
    }
  };

  const openPaymentModal = (fee) => {
    setPaymentFee(fee);
    setPaymentAmount(String(balanceOf(fee)));
    setPaymentMode('cash');
    setPaymentRemarks('');
  };

  const submitPayment = async () => {
    const paidAmount = Number(paymentAmount);
    if (!Number.isFinite(paidAmount) || paidAmount <= 0) {
      setError('Please enter a valid payment amount.');
      return;
    }
    setRecordingPayment(true);
    try {
      const response = await axios.post(`${API}/fees/${paymentFee._id}/pay`, {
        paidAmount,
        paymentMode,
        remarks: paymentRemarks || 'Admin recorded payment',
      });
      flash(
        response.data.latestReceiptNumber
          ? `Payment recorded. Receipt ${response.data.latestReceiptNumber} is ready.`
          : 'Payment recorded successfully.'
      );
      setPaymentFee(null);
      fetchFees();
      fetchStats();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Unable to record payment.');
    } finally {
      setRecordingPayment(false);
    }
  };

  const sendReminder = async (fee) => {
    try {
      setReminderLoadingId(fee._id);
      const response = await axios.post(`${API}/fees/send-reminder`, {
        studentId: fee.studentId?._id,
        feeIds: [fee._id],
        channel: 'sms',
      });
      flash(response.data.message || 'SMS reminder sent successfully.');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Unable to send SMS reminder.');
    } finally {
      setReminderLoadingId('');
    }
  };

  const sendBulkReminders = async () => {
    if (!window.confirm('Send SMS reminders to all parents with pending fees?')) return;
    try {
      setBulkReminderLoading(true);
      const response = await axios.post(`${API}/fees/send-bulk-reminders`, { channel: 'sms' });
      flash(response.data.message || 'SMS reminders sent successfully.');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Unable to send bulk reminders.');
    } finally {
      setBulkReminderLoading(false);
    }
  };

  const viewReceipt = async (feeId) => {
    try {
      setReceiptLoadingId(feeId);
      const response = await axios.get(`${API}/fees/${feeId}/receipt`);
      setReceiptModal({ feeId, data: response.data });
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Unable to load receipt.');
    } finally {
      setReceiptLoadingId('');
    }
  };

  const downloadReceipt = async (feeId) => {
    try {
      setDownloadLoadingId(feeId);
      const response = await axios.get(`${API}/fees/${feeId}/receipt/download`, {
        responseType: 'blob',
      });
      const fileName = downloadName(response.headers, `receipt-${feeId}.html`);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Unable to download receipt.');
    } finally {
      setDownloadLoadingId('');
    }
  };

  const visibleFees = filters.feeGroup
    ? fees.filter((fee) => fee.studentId?.feeGroup === filters.feeGroup)
    : fees;

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="sticky top-0 z-10 bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Fee Management</h1>
              <p className="text-sm text-gray-500">Track payments, receipts, and admin-only SMS reminders.</p>
            </div>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          {error && <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-red-700">{error}</div>}
          {success && <div className="mb-6 rounded-lg border border-green-300 bg-green-50 p-4 text-green-700">{success}</div>}

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <div className="rounded-lg bg-white p-6 shadow"><div className="text-sm text-gray-500">Total Collected</div><div className="mt-2 text-2xl font-bold text-green-700">{money(stats.totalCollected)}</div></div>
            <div className="rounded-lg bg-white p-6 shadow"><div className="text-sm text-gray-500">Pending Balance</div><div className="mt-2 text-2xl font-bold text-amber-700">{money(stats.pendingAmount)}</div></div>
            <div className="rounded-lg bg-white p-6 shadow"><div className="text-sm text-gray-500">Paid Records</div><div className="mt-2 text-2xl font-bold text-indigo-700">{stats.paidCount || 0}</div></div>
            <div className="rounded-lg bg-white p-6 shadow"><div className="text-sm text-gray-500">Pending Records</div><div className="mt-2 text-2xl font-bold text-rose-700">{stats.pendingCount || 0}</div></div>
            <div className="rounded-lg bg-white p-6 shadow"><div className="text-sm text-gray-500">Overdue Records</div><div className="mt-2 text-2xl font-bold text-red-700">{stats.overdueCount || 0}</div></div>
          </div>

          <div className="mb-6 rounded-lg bg-white p-6 shadow">
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => { resetForm(); setShowModal(true); }} className="rounded-md bg-indigo-600 px-4 py-2 font-medium text-white hover:bg-indigo-700">Add Fee Record</button>
              <button type="button" onClick={() => { resetBulkForm(); setShowBulkModal(true); }} className="rounded-md bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700">Bulk Create Fees</button>
              <button type="button" onClick={sendBulkReminders} disabled={bulkReminderLoading} className="rounded-md bg-amber-500 px-4 py-2 font-medium text-white hover:bg-amber-600 disabled:opacity-60">{bulkReminderLoading ? 'Sending SMS...' : 'Send Pending SMS Reminders'}</button>
              <button type="button" onClick={() => setShowOpeningBalanceModal(true)} className="rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 hover:bg-gray-50">Import Opening Balances</button>
            </div>
            <div className="mt-4 rounded-md border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-800">Only admins can send fee reminder SMS messages to parents.</div>
          </div>

          <div className="mb-6 rounded-lg bg-white p-6 shadow">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              <select value={filters.status} onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))} className="rounded-md border border-gray-300 px-3 py-2">
                <option value="">All Statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="partial">Partial</option>
              </select>
              <select value={filters.studentId} onChange={(e) => setFilters((current) => ({ ...current, studentId: e.target.value }))} className="rounded-md border border-gray-300 px-3 py-2">
                <option value="">All Students</option>
                {students.map((student) => <option key={student._id} value={student._id}>{student.name} ({student.rollNumber})</option>)}
              </select>
              <select value={filters.feeGroup} onChange={(e) => setFilters((current) => ({ ...current, feeGroup: e.target.value }))} className="rounded-md border border-gray-300 px-3 py-2">
                <option value="">All Fee Groups</option>
                {feeGroups.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <input type="date" value={filters.fromDate} onChange={(e) => setFilters((current) => ({ ...current, fromDate: e.target.value }))} className="rounded-md border border-gray-300 px-3 py-2" />
              <input type="date" value={filters.toDate} onChange={(e) => setFilters((current) => ({ ...current, toDate: e.target.value }))} className="rounded-md border border-gray-300 px-3 py-2" />
            </div>
          </div>

          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="border-b px-6 py-4"><h2 className="text-lg font-semibold text-gray-800">Student Fee Records</h2></div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Student</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Fee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Paid / Balance</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Receipt</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {visibleFees.length === 0 ? (
                    <tr><td colSpan="7" className="px-6 py-10 text-center text-gray-500">No fee records found.</td></tr>
                  ) : visibleFees.map((fee) => (
                    <tr key={fee._id} className="hover:bg-gray-50">
                      <td className="px-4 py-4"><div className="font-medium text-gray-900">{fee.studentId?.name || 'Unknown'}</div><div className="text-xs text-gray-500">{fee.studentId?.rollNumber || '-'}</div></td>
                      <td className="px-4 py-4"><div className="font-medium capitalize text-gray-900">{fee.feeType?.replace('_', ' ')}</div><div className="mt-1"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(fee.status)}`}>{fee.status}</span></div></td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        {money(fee.amount)}
                        {fee.lateFeeAmount > 0 && <div className="mt-1 text-xs font-medium text-red-600">+ {money(fee.lateFeeAmount)} late fee</div>}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700"><div>{money(fee.paidAmount)}</div><div className="text-xs text-gray-500">Balance: {money(balanceOf(fee))}</div></td>
                      <td className="px-4 py-4 text-sm text-gray-700">{fee.dueDate ? new Date(fee.dueDate).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {Number(fee.paidAmount || 0) > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => viewReceipt(fee._id)} disabled={receiptLoadingId === fee._id} className="rounded-md border border-indigo-200 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">{receiptLoadingId === fee._id ? 'Loading...' : 'View'}</button>
                            <button type="button" onClick={() => downloadReceipt(fee._id)} disabled={downloadLoadingId === fee._id} className="rounded-md border border-green-200 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50">{downloadLoadingId === fee._id ? 'Downloading...' : 'Download'}</button>
                          </div>
                        ) : <span className="text-gray-400">No receipt yet</span>}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex flex-wrap gap-2">
                          {balanceOf(fee) > 0 && <button type="button" onClick={() => openPaymentModal(fee)} className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700">Record Payment</button>}
                          {balanceOf(fee) > 0 && fee.studentId?._id && <button type="button" onClick={() => sendReminder(fee)} disabled={reminderLoadingId === fee._id} className="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50">{reminderLoadingId === fee._id ? 'Sending...' : 'SMS Reminder'}</button>}
                          <button type="button" onClick={() => editFee(fee)} className="rounded-md border border-indigo-200 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50">Edit</button>
                          <button type="button" onClick={() => deleteFee(fee._id)} className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-semibold text-gray-800">{editingFee ? 'Edit Fee Record' : 'Add Fee Record'}</h3><button type="button" onClick={() => setShowModal(false)} className="text-xl text-gray-500 hover:text-gray-700">x</button></div>
            <form onSubmit={saveFee} className="space-y-4">
              <div><label className="mb-1 block text-sm font-medium text-gray-700">Student</label><select required value={formData.studentId} onChange={(e) => setFormData((current) => ({ ...current, studentId: e.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2"><option value="">Select Student</option>{students.map((student) => <option key={student._id} value={student._id}>{student.name} ({student.rollNumber})</option>)}</select></div>
              <div><label className="mb-1 block text-sm font-medium text-gray-700">Fee Type</label><select value={formData.feeType} onChange={(e) => setFormData((current) => ({ ...current, feeType: e.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2"><option value="tuition">Tuition</option><option value="transport">Transport</option><option value="library">Library</option><option value="sports">Sports</option><option value="other">Other</option></select></div>
              <div><label className="mb-1 block text-sm font-medium text-gray-700">Amount</label><input type="number" min="0" required value={formData.amount} onChange={(e) => setFormData((current) => ({ ...current, amount: e.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2" /></div>
              <div><label className="mb-1 block text-sm font-medium text-gray-700">Due Date</label><DatePicker selected={formData.dueDate} onChange={(date) => setFormData((current) => ({ ...current, dueDate: date }))} className="w-full rounded-md border border-gray-300 px-3 py-2" dateFormat="yyyy-MM-dd" /></div>
              <div><label className="mb-1 block text-sm font-medium text-gray-700">Remarks</label><textarea rows="3" value={formData.remarks} onChange={(e) => setFormData((current) => ({ ...current, remarks: e.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2" /></div>
              <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={() => setShowModal(false)} className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">Cancel</button><button type="submit" className="rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700">{editingFee ? 'Update Fee' : 'Save Fee'}</button></div>
            </form>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-semibold text-gray-800">Bulk Create Fees</h3><button type="button" onClick={() => setShowBulkModal(false)} className="text-xl text-gray-500 hover:text-gray-700">x</button></div>
            <form onSubmit={saveBulkFees} className="space-y-4">
              <div><label className="mb-1 block text-sm font-medium text-gray-700">Class</label><select required value={bulkFormData.classId} onChange={(e) => setBulkFormData((current) => ({ ...current, classId: e.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2"><option value="">Select Class</option>{classes.map((classItem) => <option key={classItem._id} value={classItem._id}>{classItem.className} - {classItem.section}</option>)}</select></div>
              <div><label className="mb-1 block text-sm font-medium text-gray-700">Fee Type</label><select value={bulkFormData.feeType} onChange={(e) => setBulkFormData((current) => ({ ...current, feeType: e.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2"><option value="tuition">Tuition</option><option value="transport">Transport</option><option value="library">Library</option><option value="sports">Sports</option><option value="other">Other</option></select></div>
              <div><label className="mb-1 block text-sm font-medium text-gray-700">Amount</label><input type="number" min="0" required value={bulkFormData.amount} onChange={(e) => setBulkFormData((current) => ({ ...current, amount: e.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2" /></div>
              <div><label className="mb-1 block text-sm font-medium text-gray-700">Due Date</label><DatePicker selected={bulkFormData.dueDate} onChange={(date) => setBulkFormData((current) => ({ ...current, dueDate: date }))} className="w-full rounded-md border border-gray-300 px-3 py-2" dateFormat="yyyy-MM-dd" /></div>
              <div><label className="mb-1 block text-sm font-medium text-gray-700">Description</label><textarea rows="3" value={bulkFormData.description} onChange={(e) => setBulkFormData((current) => ({ ...current, description: e.target.value }))} className="w-full rounded-md border border-gray-300 px-3 py-2" /></div>
              <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={() => setShowBulkModal(false)} className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">Cancel</button><button type="submit" className="rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700">Create Fees</button></div>
            </form>
          </div>
        </div>
      )}

      {receiptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div><h3 className="text-lg font-semibold text-gray-800">Receipt Preview</h3><p className="text-sm text-gray-500">{receiptModal.data.receiptNumber}</p></div>
              <div className="flex gap-3">
                <button type="button" onClick={() => downloadReceipt(receiptModal.feeId)} className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700">Download</button>
                <button type="button" onClick={() => setReceiptModal(null)} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Close</button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 px-6 py-6 md:grid-cols-2">
              <div className="rounded-lg bg-gray-50 p-4"><div className="text-xs uppercase tracking-wide text-gray-500">Student</div><div className="mt-2 text-lg font-semibold text-gray-900">{receiptModal.data.student?.name}</div><div className="text-sm text-gray-600">Roll No: {receiptModal.data.student?.rollNumber}</div></div>
              <div className="rounded-lg bg-gray-50 p-4"><div className="text-xs uppercase tracking-wide text-gray-500">Parent</div><div className="mt-2 text-lg font-semibold text-gray-900">{receiptModal.data.parent?.name}</div><div className="text-sm text-gray-600">{receiptModal.data.parent?.phone}</div><div className="text-sm text-gray-600">{receiptModal.data.parent?.email}</div></div>
              <div className="rounded-lg bg-gray-50 p-4"><div className="text-xs uppercase tracking-wide text-gray-500">Payment Summary</div><div className="mt-2 space-y-1 text-sm text-gray-700"><div>Fee Type: <span className="font-medium capitalize">{receiptModal.data.feeType}</span></div><div>Paid Amount: <span className="font-medium">{money(receiptModal.data.paidAmount)}</span></div><div>Total Fee: <span className="font-medium">{money(receiptModal.data.feeAmount)}</span></div><div>Balance: <span className="font-medium">{money(receiptModal.data.balanceAmount)}</span></div></div></div>
              <div className="rounded-lg bg-gray-50 p-4"><div className="text-xs uppercase tracking-wide text-gray-500">Transaction</div><div className="mt-2 space-y-1 text-sm text-gray-700"><div>Mode: <span className="font-medium">{receiptModal.data.paymentMode}</span></div><div>Transaction ID: <span className="font-medium">{receiptModal.data.transactionId}</span></div><div>Issued On: <span className="font-medium">{new Date(receiptModal.data.receiptIssuedAt).toLocaleString()}</span></div></div></div>
            </div>
            <div className="border-t px-6 py-6">
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Payment History</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Receipt</th><th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Date</th><th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Mode</th><th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Amount</th><th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Balance</th></tr></thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {(receiptModal.data.paymentHistory || []).map((record) => <tr key={`${record.receiptNumber}-${record.paidDate}`}><td className="px-3 py-3 text-sm text-gray-700">{record.receiptNumber}</td><td className="px-3 py-3 text-sm text-gray-700">{record.paidDate ? new Date(record.paidDate).toLocaleDateString() : '-'}</td><td className="px-3 py-3 text-sm text-gray-700">{record.paymentMode || '-'}</td><td className="px-3 py-3 text-sm text-gray-700">{money(record.amount)}</td><td className="px-3 py-3 text-sm text-gray-700">{money(record.balanceAfterPayment)}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      {paymentFee && (
        <Modal
          title="Record Payment"
          subtitle={`${paymentFee.studentId?.name || 'Unknown'} — ${paymentFee.feeType?.replace('_', ' ')}`}
          onClose={() => setPaymentFee(null)}
          footer={
            <>
              <button type="button" onClick={() => setPaymentFee(null)} className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="button" onClick={submitPayment} disabled={recordingPayment} className="rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-50">
                {recordingPayment ? 'Recording…' : 'Record Payment'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700 space-y-1">
              <div>Fee Amount: <span className="font-medium">{money(paymentFee.amount)}</span></div>
              {paymentFee.lateFeeAmount > 0 && <div className="text-red-600">Late Fee: <span className="font-medium">{money(paymentFee.lateFeeAmount)}</span></div>}
              <div>Already Paid: <span className="font-medium">{money(paymentFee.paidAmount)}</span></div>
              <div>Balance Due: <span className="font-medium">{money(balanceOf(paymentFee))}</span></div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Amount Received</label>
              <input type="number" min="0" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Payment Mode</label>
              <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2">
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
                <option value="online">Online</option>
                <option value="upi">UPI</option>
                <option value="net_banking">Net Banking</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Remarks</label>
              <input type="text" value={paymentRemarks} onChange={(e) => setPaymentRemarks(e.target.value)} placeholder="Admin recorded payment" className="w-full rounded-md border border-gray-300 px-3 py-2" />
            </div>
          </div>
        </Modal>
      )}

      {showOpeningBalanceModal && (
        <OpeningBalanceImportModal
          onClose={() => setShowOpeningBalanceModal(false)}
          onImported={() => { fetchFees(); fetchStats(); }}
        />
      )}
    </div>
  );
};

export default AdminFees;
